/**
 * TypeScript types mirroring the V2-2-U3a SavedScreen predicate AST.
 *
 * Discriminated unions on ``op`` so callers can narrow without manual
 * type guards. The backend re-validates every persisted criteria
 * through ``ScreenCriteria.__post_init__`` so the API rejects any
 * shape that doesn't match these types with a 400; the types are not
 * load-bearing for security, only for ergonomics.
 *
 * Since V2-2-U3c the SQL translator covers the full 15-field surface
 * (7 header + 8 V2-U2 derived). The Screen Builder UI surfaces both
 * groups in its field dropdown via the ``SCREEN_ALL_FIELDS`` export.
 */

export const SCREEN_HEADER_FIELDS = [
  "trade_id",
  "opened_at",
  "closed_at",
  "account_id",
  "strategy_id",
  "currency",
  "status",
] as const;

export type ScreenHeaderField = (typeof SCREEN_HEADER_FIELDS)[number];

export const SCREEN_DERIVED_FIELDS = [
  "realized_pnl_amount",
  "opening_notional_amount",
  "quantity_total",
  "fees_amount",
  "realized_return_pct",
  "hold_duration_seconds",
  "tag_labels_csv",
  "r_multiple",
] as const;

export type ScreenDerivedField = (typeof SCREEN_DERIVED_FIELDS)[number];

export type ScreenField = ScreenHeaderField | ScreenDerivedField;

export type ScreenFieldType = "numeric" | "string" | "datetime" | "uuid" | "enum";

export const SCREEN_FIELD_TYPES: Record<ScreenField, ScreenFieldType> = {
  trade_id: "uuid",
  opened_at: "datetime",
  closed_at: "datetime",
  account_id: "uuid",
  strategy_id: "uuid",
  currency: "string",
  status: "enum",
  realized_pnl_amount: "numeric",
  opening_notional_amount: "numeric",
  quantity_total: "numeric",
  fees_amount: "numeric",
  realized_return_pct: "numeric",
  hold_duration_seconds: "numeric",
  tag_labels_csv: "string",
  r_multiple: "numeric",
};

export const STATUS_ENUM_VALUES = ["OPEN", "CLOSED", "PARTIAL"] as const;
export type StatusEnumValue = (typeof STATUS_ENUM_VALUES)[number];

export type CompoundOp = "and" | "or" | "not";

export type LeafOp =
  | "eq"
  | "ne"
  | "lt"
  | "le"
  | "gt"
  | "ge"
  | "in"
  | "contains"
  | "is_null"
  | "is_not_null";

export type ScreenLeaf =
  | {
      op: "eq" | "ne" | "lt" | "le" | "gt" | "ge" | "contains";
      field: ScreenField;
      value: string | number;
    }
  | {
      op: "in";
      field: ScreenField;
      value: ReadonlyArray<string | number>;
    }
  | {
      op: "is_null" | "is_not_null";
      field: ScreenField;
    };

export type ScreenCompound = {
  op: CompoundOp;
  terms: ReadonlyArray<ScreenPredicate>;
};

export type ScreenPredicate = ScreenLeaf | ScreenCompound;

export const MATCH_ALL_CRITERIA: ScreenPredicate = { op: "and", terms: [] };

/**
 * Human-facing labels for each operator. The persisted value remains the
 * canonical code (``eq``, ``gt`` …); only the rendered label is the symbol,
 * so saved criteria and the API contract are unaffected.
 */
export const OP_LABELS: Record<LeafOp, string> = {
  eq: "=",
  ne: "≠",
  lt: "<",
  le: "≤",
  gt: ">",
  ge: "≥",
  in: "is one of",
  contains: "contains",
  is_null: "is empty",
  is_not_null: "is not empty",
};

export const OPS_BY_TYPE: Record<ScreenFieldType, ReadonlyArray<LeafOp>> = {
  numeric: ["eq", "ne", "lt", "le", "gt", "ge", "in", "is_null", "is_not_null"],
  string: ["eq", "ne", "in", "contains", "is_null", "is_not_null"],
  datetime: ["eq", "ne", "lt", "le", "gt", "ge", "is_null", "is_not_null"],
  uuid: ["eq", "ne", "in", "is_null", "is_not_null"],
  enum: ["eq", "ne", "in"],
};
